/** @jsx jsx */
import React, { useCallback } from 'react';
import { Stack, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Form, { FormFooter, FormSection } from '@atlaskit/form';
import ButtonGroup from '@atlaskit/button/button-group';
import Button, { LinkButton } from '@atlaskit/button/new';
import type { ValueType } from '@atlaskit/select';
import SectionMessage from '@atlaskit/section-message';
import { useRouterActions } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type {
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkRemoteOAuth2ApplicationLink,
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
} from '../../../common/entities.tsx';
import type { SelectOption } from '../../../common/types.tsx';
import { ApplicationUrl, DisplayName, DisplayUrl } from '../../common/fields/index.tsx';
import { putOAuth2ConfigInfo, updateApplinkV3 } from '../../../rest/index.tsx';
import {
	APPLINKS_LIST_PAGE_URL,
	APPLINKS_LIST_PAGE_URL_LEGACY,
} from '../../../common/constants.tsx';
import messages from '../messages.tsx';
import commonMessages from '../../messages.tsx';
import CredentialsForm from './credentials-form/CredentialsForm.tsx';

const styles = cssMap({
	dividerStyle: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		width: '100%',
		marginTop: token('space.200'),
	},
	formSectionInner: {
		marginTop: token('space.200'),
	},
});

type OAuth2EditForm = {
	displayName: string;
	displayUrl: string;
	remoteApplicationSupportedScopes: ValueType<SelectOption, true>;
	applicationClientId?: string;
	applicationClientSecret?: string;
	redirectUrl?: string;
};

type ApplinksOAuth2EditPageContentsProps = {
	applink: ApplicationLinkV3Data;
	oauth2ConfigInfo: ApplicationLinkOAuth2ConfigInfo | null;
	partiallyLinkedMode?: boolean;
	onDisplayError: (err: string) => void;
	onRequestStatusRefresh: () => void;
};

const ApplinksOAuth2EditPageContents = (props: ApplinksOAuth2EditPageContentsProps) => {
	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const getListPageUrl = useCallback(() => {
		if (fg('applinks-933-experimental-list-page')) {
			return APPLINKS_LIST_PAGE_URL;
		}
		return APPLINKS_LIST_PAGE_URL_LEGACY;
	}, []);

	const onFormSubmit = useCallback(
		async (data: OAuth2EditForm) => {
			try {
				const applinkUpdate: ApplicationLinkV3Simple = {
					id: props.applink.id,
					type: props.applink.type,
					system: props.applink.system,
					primary: props.applink.primary,
					isExtraAuthRequired: props.applink.isExtraAuthRequired,
					rpcUrl: props.applink.rpcUrl,

					// Only change these properties:
					name: data.displayName,
					displayUrl: data.displayUrl,
				};

				await updateApplinkV3(props.applink.id, applinkUpdate);

				if (props.oauth2ConfigInfo !== null) {
					// Check at least one param is defined
					if (data.applicationClientId || data.applicationClientSecret || data.redirectUrl) {
						const scopes = data.remoteApplicationSupportedScopes
							? data.remoteApplicationSupportedScopes.map((scope) => scope.value)
							: [];

						const oauth2ConfigUpdate: ApplicationLinkRemoteOAuth2ApplicationLink = {
							remoteClientId: data.applicationClientId,
							remoteClientSecret: data.applicationClientSecret,
							remoteRedirectUrl: data.redirectUrl,
							outboundScopes: scopes,
						};
						await putOAuth2ConfigInfo(props.applink.id, oauth2ConfigUpdate);
					}
				}

				push(getListPageUrl());
			} catch (err) {
				const maybeError = err instanceof Error ? err : undefined;
				// If you see this error:
				// - partially linked variant = outgoing credentials are inline
				// - normal variant = outgoing credentials are submitted via a separate modal (outgoing-credentials-modal)
				log.safeErrorWithoutCustomerData(
					'applinks.administration.edit.oauth2.submitEditForm',
					`Failed to update OAuth 2.0 applink ${props.partiallyLinkedMode ? 'using partially linked variant' : 'using normal variant'} of the edit form`,
					maybeError,
				);
				props.onDisplayError(
					maybeError ? maybeError.message : formatMessage(commonMessages.unknownErrorText),
				);
			}
		},
		[formatMessage, getListPageUrl, props, push],
	);

	return (
		<>
			<Form<OAuth2EditForm> onSubmit={onFormSubmit}>
				{({ formProps, submitting }) => (
					<form {...formProps}>
						<Stack space="space.200">
							<FormSection title={formatMessage(messages.linkDetailsSectionHeading)}>
								<Stack space="space.200" xcss={styles.formSectionInner}>
									<DisplayName name="displayName" defaultName={props.applink.name} />
									<DisplayUrl name="displayUrl" defaultUrl={props.applink.displayUrl} />
									<ApplicationUrl name="rpcUrl" defaultUrl={props.applink.rpcUrl} disabled />
								</Stack>
							</FormSection>
							<hr role="presentation" css={styles.dividerStyle} />
							{props.partiallyLinkedMode && (
								<SectionMessage title={formatMessage(messages.continueLinkingTitle)}>
									<Text>{formatMessage(messages.continueLinkingDescription)}</Text>
								</SectionMessage>
							)}

							{props.oauth2ConfigInfo !== null && (
								<CredentialsForm
									{...props}
									oauth2ConfigInfo={props.oauth2ConfigInfo}
									onRequestStatusRefresh={props.onRequestStatusRefresh}
								/>
							)}
							{!props.oauth2ConfigInfo && (
								<SectionMessage
									appearance="error"
									title={formatMessage(messages.oauth2ConfigLoadFailedTitle)}
								>
									{formatMessage(messages.oauth2ConfigLoadFailedDescription)}
								</SectionMessage>
							)}

							<FormFooter>
								<ButtonGroup>
									<LinkButton href={getListPageUrl()} appearance="subtle">
										{formatMessage(messages.formCancelButtonText)}
									</LinkButton>
									<Button
										testId="applinks-administration.ui.edit.oauth2.submit-button"
										type="submit"
										appearance="primary"
										isLoading={submitting}
									>
										{formatMessage(messages.formSaveButtonText)}
									</Button>
								</ButtonGroup>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

export default ApplinksOAuth2EditPageContents;
